import { useEffect, useCallback, useRef, useMemo } from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  createColumn,
  getForm,
  getRegister,
  formatDateTime,
  createViewAction,
  addSelectItem,
  downloadThroughAnchorLink,
  beautify,
  createRenewAction,
  numberFormat,
  createDeactivateAction,
} from "../../utils/helpers";
import useGet from "../common/useGet";
import {
  API_ALL_CAR_TYPES,
  API_BOOKING_RESERVATION_RATES_PAGINATED,
  API_CONFIGURABLES_BOOKING,
  API_DISTRICTS,
  API_PARKINGS,
  API_STICKERS_DOWNLOAD,
  API_STREETS,
} from "../../utils/apis";
import {
  ADVANCE,
  INDIVIDUAL,
  ORGANISATION,
  RESERVATION,
  RESERVATION_TYPE,
  STICKER,
  STICKER_TYPE,
  DAILY,
  WEEKLY,
  MONTHLY,
} from "../../utils/constants";
import { TFormHookParams } from "../../types/common";
import { Button } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { IsManager, IsAccountant } from "../../utils/permissions";
import { useAuth } from "../../context/AuthContext";
import {
  URL_BOOKINGS,
  URL_REGISTER_VIEW_STICKER_BOOKING,
} from "../../utils/urls";
import { Table } from "../../components/common/Table";

const resolveDurationType = (durationType: string) => {
  switch (durationType) {
    case DAILY:
      return "Days";
    case WEEKLY:
      return "Weeks";
    case MONTHLY:
      return "Months";
    default:
      return durationType;
  }
};

export const useBooking = () => {
  const isManager = IsManager();
  const isAccountant = IsAccountant();
  const navigate = useNavigate();

  // The register layout already fetches API_BOOKINGS_PAGINATED with the active
  // filters, so this hook must not fetch it again.

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const bookingType =
    location.state?.filterParams?.booking_type ||
    location.state?.filterParams?.status ||
    searchParams.get("booking_type") ||
    searchParams.get("status");

  // Extract status from URL pathname (e.g., /register/bookings/confirmed)
  const pathParts = location.pathname.split("/");
  const bookingStatus = pathParts[pathParts.length - 1] || "";

  const getColumnsByType = (type) => {
    switch (type) {
      case STICKER:
        return [
          createColumn("booking_number", true),
          createColumn("invoice_amount", false),
          createColumn("customer_name", true),
          createColumn("phone_number", false),
          createColumn("view_sticker", false),
          createColumn("email", false),
          createColumn("type_name", false),
          createColumn("car_number", true, "Number Plate"),
          createColumn("car_type_name", true, "Car Type Name"),
          createColumn("city", false),
          createColumn("type_name", true, "Booking Type"),
          createColumn("payment_status", false),
          createColumn("actions", false),
        ];

      case ADVANCE:
        return [
          createColumn("booking_number", true),
          createColumn("invoice_amount", false),
          createColumn("customer_name", true),
          createColumn("phone_number", true),
          createColumn("email", true),
          createColumn("city", false),
          createColumn("type_name", true, "Booking Type"),
          createColumn("payment_status", false),
          createColumn("actions", false),
        ];

      case RESERVATION:
        return [
          createColumn("booking_number", true),
          createColumn("type_name", false),
          createColumn("invoice_amount", false),
          createColumn("customer_name", true),
          createColumn("phone_number", true),
          createColumn("email", true),
          createColumn("city", false),
          createColumn("type_name", true, "Booking Type"),
          createColumn("payment_status", false),
          createColumn("actions", false),
        ];

      default:
        return [
          createColumn("booking_number", true),
          createColumn("invoice_amount", false),
          createColumn("customer_name", true),
          createColumn("phone_number", true),
          createColumn("email", true),
          createColumn("type_name", true, "Booking Type"),
          createColumn("city_name", false),
          createColumn("payment_status", false),
          createColumn("actions", false),
        ];
    }
  };

  // FIXED: Pass bookingType to getColumnsByType
  const tableColumns = () => [...getColumnsByType(bookingType)];

  const tableData = (data) => {
    if (!data) return [];
    return data.map((item) => {
      // Check if renew action should be visible:
      // Only show for STICKER or RESERVATION types when viewing the confirmed register
      const shouldShowRenew =
        (bookingType === STICKER || bookingType === RESERVATION) &&
        bookingStatus === "confirmed";

      return {
        ...item,
        customer_name: item?.customer_name,
        is_organization: item.is_organization,
        city: item.city_name,
        payment_status: beautify(item?.payment_status),
        invoice_amount: numberFormat(item?.invoice_amount),
        created_at: formatDateTime(item.created_at),
        updated_at: formatDateTime(item.updated_at),
        view_sticker: (
          <Button
            onClick={() =>
              navigate(URL_REGISTER_VIEW_STICKER_BOOKING, { state: { item } })
            }
          >
            View Sticker
          </Button>
        ),
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_BOOKINGS, item?.id),
              ...(shouldShowRenew
                ? [createRenewAction(URL_BOOKINGS, item?.id)]
                : []),
              // createViewStickerAction(URL_BOOKINGS, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    // const {
    //   downloadFile,
    //   success: downloadSuccess,
    //   error: downloadError,
    //   downloading: isDownloading,
    // } = useDownload();

    // useEffect(() => {
    //   if (downloadSuccess) {
    //     message.success("File downloaded successfully");
    //   }

    //   if (Object?.keys(downloadError)?.length > 0) {
    //     message.error(downloadError?.message || "Failed to download file");
    //   }
    // }, [downloadSuccess, downloadError]);

    // const handleDownloadReceipt = (sticker) => {
    //   const fileName = `Receipt_${sticker.sticker_number}_${Date.now()}.pdf`;
    //   downloadFile({
    //     api: `${API_STICKERS_DOWNLOAD}${sticker.id}/receipt/`,
    //     filename: fileName,
    //   });
    // };

    const stickerTableColumns = [
      createColumn("sticker_number", true, "Sticker Number"),
      createColumn("booking", false, "Booking ID"),
      createColumn("updated_by", false, "Updated By"),
      createColumn("updated_at", false, "Updated At"),
      createColumn("deactivated_at", false, "Deactivated At"),
      createColumn("created_at", false, "Created At"),
      createColumn("receipt", false, "Receipt"),
      createColumn("actions", false, "Actions"),
    ];

    const stickerTableData = item?.stickers?.map((sticker) => ({
      ...sticker,
      created_at: formatDateTime(sticker.created_at),
      updated_at: formatDateTime(sticker.updated_at),
      deactivated_at: formatDateTime(sticker.deactivated_at),
      // receipt: (
      //   <Button
      //     color="primary"
      //     size="large"
      //     variant="outlined"
      //     // loading={isDownloading}
      //     disabled={isDownloading}
      //     onClick={() => handleDownloadReceipt(sticker)}
      //   >
      //     {isDownloading ? "Downloading..." : "Download Sticker"}
      //   </Button>
      // ),
      actions: (
        <FormActions
          actions={[
            createDeactivateAction(
              `${API_STICKERS_DOWNLOAD}${sticker.id}/deactivate/`,
              `sticker ${sticker.sticker_number}`,
              {},
              {
                showReasonField: false,
                method: "PATCH",
              },
            ),
          ]}
        />
      ),
    }));

    return {
      sections: [
        {
          title: "Booking Information",
          fields: {
            booking_number: item.booking_number,
            booking_type: item.booking_type,
            status: item.status,
          },
        },

        {
          title: "Customer Details",
          fields: {
            // Show organization_name if is_organization, otherwise show first_name and last_name
            ...(item.is_organization
              ? {
                  organization_name: item.organization_name || "Not provided",
                }
              : {
                  first_name: item.first_name || "Not provided",
                  last_name: item.last_name || "Not provided",
                }),
            phone_number: item.phone_number,
            email: item.email || "Not provided",
            is_organization: item.is_organization ? "Yes" : "No",
          },
        },

        ...(item?.attachments?.length > 0 && item.attachments[0]?.file
          ? [
              {
                title: "Attachment Details",
                fields: {
                  caption: (
                    <Button
                      color="primary"
                      size="large"
                      variant="outlined"
                      onClick={() => {
                        const fileUrl = item.attachments[0].file;
                        if (fileUrl) {
                          const now = new Date();
                          const formattedDate = now.toISOString().slice(0, 10);
                          const filename = `Invoice_${formattedDate}.pdf`;
                          downloadThroughAnchorLink(fileUrl, filename);
                        }
                      }}
                    >
                      Download Invoice
                    </Button>
                  ),
                },
              },
            ]
          : []),

        {
          title: "Booking Details",
          fields: {
            invoice_number: item.invoice_number,
            // Show number_of_slots only for RESERVATION bookings
            ...(item.booking_type === RESERVATION && {
              number_of_slots: item.number_of_slots || "N/A",
            }),
            // Show number_of_stickers only for STICKER bookings
            ...(item.booking_type === STICKER && {
              number_of_stickers: item.number_of_stickers || "N/A",
            }),
            // Show duration for STICKER and RESERVATION, but not ADVANCE
            ...(item.booking_type !== ADVANCE && {
              duration: item.duration
                ? `${item.duration} ${resolveDurationType(item.duration_type)}`
                : "N/A",
            }),
            // Always show invoice_amount, city, and car_size
            invoice_amount: Math.round(item?.invoice_amount) || "N/A",
            city: item.city_name,
            car_size: item.car_size_name,
            car_number: item.car_number || "N/A",
            ...(item.booking_type === RESERVATION && {
              reserved_parking_name: item?.reserved_parking_name,
            }),
          },
        },
        {
          title: "Stickers Information",
          fields: {},
          component: (
            <Table
              tableTitle="Stickers"
              count={tableData.length}
              tableColumns={stickerTableColumns ? stickerTableColumns : []}
              tableData={stickerTableData ?? []}
              loading={false}
            />
          ),
        },
        {
          title: "System Information",
          fields: {
            record_id: item.id,
            created_at: formatDateTime(item.created_at),
            updated_at: formatDateTime(item.updated_at),
            created_by: item.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : "System",
            updated_by: item.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : "Not updated",
            start_date: formatDateTime(item.start_date),
            end_date: formatDateTime(item.end_date),
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_BOOKINGS);
  const activityFilter = "bookings";

  return {
    tableData,
    tableColumns,
    viewData,
    activityFilter,
    formRoute: isManager || isAccountant ? null : formRoute,
  };
};

export const useBookingForm = ({
  form = {},
  updateFormValues,
}: TFormHookParams = {}) => {
  const prevValuesRef = useRef({
    city: null,
    street: null,
    reserved_parkings: null,
    booking_type: null,
    sticker_type: null,
    duration_type: null,
  });

  const {
    loadData: loadCities,
    loading: loadingCities,
    data: cities,
  } = useGet();

  // Add separate hook for street-filtered parkings
  const {
    loadData: loadParkingsUnderStreet,
    loading: loadingParkingsUnderStreet,
    data: parkingsUnderStreet,
  } = useGet();

  const {
    loadData: loadStreetsUnderACity,
    loading: loadingStreets,
    data: streetsData,
  } = useGet();

  const { user } = useAuth();

  //MARK: - For Account or Manager, Only show Mbarara and Fort Portal City as cities
  const filteredCities = useMemo(() => {
    if (!Array.isArray(cities) || cities.length === 0) {
      return [];
    }
    const districtId = user?.profile?.district;
    // Match user’s district if available
    const userCities =
      districtId != null
        ? cities.filter((city) => String(city.id) === String(districtId))
        : [];

    // Return matched district or all cities
    return userCities.length > 0 ? userCities : cities;
  }, [cities, user]);

  const {
    loadData: loadStickerType,
    loading: loadingStickerTypes,
    data: stickerTypesData,
  } = useGet();

  const {
    loadData: loadCarTypes,
    loading: loadingCarTypes,
    data: carTypes,
  } = useGet();

  const {
    loadData: loadReservationType,
    loading: loadingReservationTypes,
    data: reservationTypesData,
  } = useGet();

  // Hook for parking rates
  const {
    loadData: loadParkingRateForCarSizeAndParking,
    loading: loadingParkingRateForCarSizeAndParking,
    data: parkingRatesData,
    error: parkingRatesError,
  } = useGet();

  const {
    booking_type,
    is_organization,
    city,
    street,
    reserved_parkings,
    number_of_slots,
    booking_rate,
    duration,
    car_size,
    backend_number_of_slots,
    start_date,
    type: sticker_type,
    duration_type,
  } = form || {};

  // Get selected parking details
  const selectedParking = parkingsUnderStreet?.results?.find(
    (parking) => parking.id === reserved_parkings,
  );

  const totalCapacity = selectedParking?.capacity || 0;
  const occupiedSlots = selectedParking?.occupied_slots || 0;
  const availableSlots = totalCapacity - occupiedSlots;
  const parkingRateAmount = parkingRatesData?.results?.[0]?.amount || null;

  // Modified calculation logic for slot amount

  const slotAmount = useMemo(() => {
    if (booking_type === STICKER) {
      // For stickers, it's just the rate amount (already includes duration consideration)
      return parseFloat(parkingRateAmount) || 0;
    } else if (booking_type === RESERVATION) {
      // For reservations: number_of_slots * rate_per_slot
      return (number_of_slots || 0) * (parseFloat(parkingRateAmount) || 0);
    }
    return 0;
  }, [booking_type, number_of_slots, parkingRateAmount]);

  // Calculate total amount payable: slot_amount * duration
  const totalAmountPayable = useMemo(() => {
    if (booking_type === STICKER) {
      // For stickers, the rate already includes duration consideration from backend
      return slotAmount * (duration || 1);
    } else if (booking_type === RESERVATION) {
      // For reservations: slot_amount * duration
      return slotAmount * (duration || 1);
    }
    return 0;
  }, [booking_type, slotAmount, duration]);

  const calculateEndDate = useCallback(
    (startDate, duration, durationType = DAILY) => {
      if (!startDate || !duration) return null;

      try {
        const start = new Date(startDate);

        // Check if the date is valid
        if (isNaN(start.getTime())) {
          console.warn("Invalid start date provided:", startDate);
          return null;
        }

        const durationNum = parseInt(duration);
        if (isNaN(durationNum) || durationNum <= 0) {
          console.warn("Invalid duration provided:", duration);
          return null;
        }

        const end = new Date(start);

        // Calculate end date based on duration type
        switch (durationType) {
          case WEEKLY:
            end.setDate(start.getDate() + durationNum * 7);
            break;
          case MONTHLY:
            // Set to same day of the month, n months later
            end.setMonth(start.getMonth() + durationNum);
            // Handle edge case where the target month has fewer days than the start month
            if (end.getDate() !== start.getDate()) {
              // Move to the last day of the previous month
              end.setDate(0);
            }
            break;
          case DAILY:
          default:
            end.setDate(start.getDate() + durationNum);
            break;
        }

        // Check if the calculated end date is valid
        if (isNaN(end.getTime())) {
          console.warn("Invalid end date calculated");
          return null;
        }

        return end.toISOString().split("T")[0];
      } catch (error) {
        console.error("Error calculating end date:", error);
        return null;
      }
    },
    [],
  );

  useEffect(() => {
    if (form?.city && form?.booking_type === RESERVATION) {
      loadReservationType({
        api: API_CONFIGURABLES_BOOKING,
        params: {
          city: form.city,
          key: RESERVATION_TYPE,
        },
      });
    }
  }, [form?.city, form?.booking_type]);

  // Load sticker types when city changes and booking type is STICKER
  useEffect(() => {
    if (form?.city && form?.booking_type === STICKER) {
      loadStickerType({
        api: API_CONFIGURABLES_BOOKING,
        params: {
          city: form.city,
          key: STICKER_TYPE,
        },
      });
    }
  }, [form?.city, form?.booking_type]);

  useEffect(() => {
    if (city && city !== prevValuesRef.current.city) {
      prevValuesRef.current.city = city;
      loadStreetsUnderACity({ api: API_STREETS, params: { district: city } });
    }
  }, [city]);

  useEffect(() => {
    if (street && street !== prevValuesRef.current.street) {
      prevValuesRef.current.street = street;
      loadParkingsUnderStreet({
        api: API_PARKINGS,
        params: { street: street },
      });
    }
  }, [street]);

  useEffect(() => {
    // Create keys that include duration_type for proper comparison
    const currentKey =
      booking_type === STICKER
        ? `${booking_type}-${city}-${sticker_type}-${duration_type}`
        : `${booking_type}-${reserved_parkings}-${city}-${duration_type}`;

    const prevKey =
      prevValuesRef.current.booking_type === STICKER
        ? `${prevValuesRef.current.booking_type}-${prevValuesRef.current.city}-${prevValuesRef.current.sticker_type}-${prevValuesRef.current.duration_type}`
        : `${prevValuesRef.current.booking_type}-${prevValuesRef.current.reserved_parkings}-${prevValuesRef.current.city}-${prevValuesRef.current.duration_type}`;

    if (currentKey === prevKey) return;

    // Update previous values
    prevValuesRef.current.booking_type = booking_type;
    prevValuesRef.current.reserved_parkings = reserved_parkings;
    prevValuesRef.current.city = city;
    prevValuesRef.current.sticker_type = sticker_type;
    prevValuesRef.current.duration_type = duration_type; // Track duration_type

    // For STICKER booking type, city, sticker_type, and duration_type are needed
    if (booking_type === STICKER && city && sticker_type && duration_type) {
      loadParkingRateForCarSizeAndParking({
        api: API_BOOKING_RESERVATION_RATES_PAGINATED,
        params: {
          booking_type: STICKER,
          city: city,
          bk_type: sticker_type,
          duration_type: duration_type, // Use duration_type from form
        },
      });
    }
    // For RESERVATION booking type, parking, city, and duration_type are needed
    else if (
      booking_type === RESERVATION &&
      reserved_parkings &&
      city &&
      duration_type
    ) {
      loadParkingRateForCarSizeAndParking({
        api: API_BOOKING_RESERVATION_RATES_PAGINATED,
        params: {
          booking_type: RESERVATION,
          parking: reserved_parkings,
          city: city,
          bk_type: sticker_type, // May not be needed for reservations
          duration_type: duration_type, // Use duration_type from form
        },
      });
    }
  }, [
    booking_type,
    reserved_parkings,
    city,
    sticker_type,
    duration_type, // Add duration_type to dependencies
    loadParkingRateForCarSizeAndParking,
  ]);

  useEffect(() => {
    if (updateFormValues && street && street !== prevValuesRef.current.street) {
      updateFormValues({
        reserved_parkings: null,
        number_of_slots: null,
        booking_rate: null,
        duration_type: null,
      });
    }
  }, [street, updateFormValues]);

  useEffect(() => {
    loadCities({
      api: API_DISTRICTS,
    });
  }, []);

  // Load car types when booking type is STICKER
  useEffect(() => {
    if (form.booking_type === STICKER) {
      loadCarTypes({
        api: API_ALL_CAR_TYPES,
      });
    }
  }, [form.booking_type]);

  // Reset slots when car size changes and auto-fill backend_number_of_slots (optimized)
  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        // number_of_slots: null,
        // backend_number_of_slots: backendSlots,
        booking_rate: null,
      });
    }
  }, [car_size, updateFormValues]);

  useEffect(() => {
    if (updateFormValues) {
      // Calculate total amount payable: slot_amount * duration
      const calculatedTotalAmount = totalAmountPayable;

      const calculatedEndDate = calculateEndDate(
        start_date,
        duration,
        duration_type,
      );

      updateFormValues({
        booking_type: booking_type,
        slot_amount: slotAmount,
        total_amount_payable: calculatedTotalAmount,
        end_date: calculatedEndDate,
        booking_rate: parkingRateAmount, // Update booking rate field
      });

      if (booking_type === STICKER) {
        updateFormValues({
          number_of_stickers: 1,
        });
      }
    }
  }, [
    updateFormValues,
    booking_type,
    backend_number_of_slots,
    number_of_slots,
    booking_rate,
    duration,
    duration_type,
    parkingRateAmount,
    start_date,
    calculateEndDate,
    slotAmount,
    totalAmountPayable,
  ]);

  useEffect(() => {
    if (
      updateFormValues &&
      start_date &&
      duration &&
      (booking_type === STICKER || booking_type === RESERVATION)
    ) {
      const calculatedEndDate = calculateEndDate(
        start_date,
        duration,
        form?.duration_type || DAILY,
      );
      if (calculatedEndDate) {
        updateFormValues({
          end_date: calculatedEndDate,
        });
      }
    }
  }, [
    booking_type,
    start_date,
    duration,
    form?.duration_type,
    updateFormValues,
    calculateEndDate,
  ]);

  useEffect(() => {}, [form?.end_date]);

  useEffect(() => {
    if (
      user?.profile?.district != null ||
      user?.profile?.district != undefined
    ) {
      updateFormValues({
        city: user?.profile?.district,
      });
    }
  }, [user]);

  // Clear fields that belong only to specific booking types
  useEffect(() => {
    if (!updateFormValues) return;

    const cleanup: Partial<Record<string, any>> = {};

    if (booking_type !== STICKER) {
      cleanup.number_of_stickers = undefined;
      cleanup.car_number = undefined;
      cleanup.car_type = undefined;
      cleanup.type = undefined;
    }

    if (booking_type !== RESERVATION) {
      cleanup.number_of_slots = undefined;
      cleanup.reserved_parkings = undefined;
      cleanup.street = undefined;
      cleanup.type = undefined;
    }

    if (booking_type !== ADVANCE) {
      cleanup.amount = undefined;
    }

    // Only update if there are fields to clean
    if (Object.keys(cleanup).length > 0) {
      updateFormValues(cleanup);
    }

    // Special case: when switching TO Sticker, default number_of_stickers to 1
    if (booking_type === STICKER && form?.number_of_stickers == null) {
      updateFormValues({ number_of_stickers: 1 });
    }
  }, [booking_type, updateFormValues, form?.number_of_stickers]);

  // Detect if we're in renew/update mode (when form has an ID)
  const isRenewMode = !!form?.id;

  // Client Details Fields
  const clientDetailFields = [
    // Organization name field
    {
      field_name: "organization_name",
      label: "Organization Name",
      type: "text",
      props: {
        required: true,
        placeholder: "Enter organization name",
        disabled: isRenewMode,
      },
      visible: is_organization !== undefined && is_organization,
    },
    // Individual fields
    {
      field_name: "first_name",
      label: "First Name",
      type: "text",
      props: { placeholder: "Enter first name", disabled: isRenewMode },
      visible: is_organization !== undefined && !is_organization,
    },
    {
      field_name: "last_name",
      label: "Last Name",
      type: "text",
      props: { placeholder: "Enter last name", disabled: isRenewMode },
      visible: is_organization !== undefined && !is_organization,
    },
    // Common fields
    {
      field_name: "phone_number",
      label: "Phone Number",
      type: "tel",
      props: {
        required: false,
        placeholder: "Enter phone number",
        disabled: isRenewMode,
      },
    },
    {
      field_name: "email",
      label: "Email Address",
      type: "email",
      props: { placeholder: "Enter email address", disabled: isRenewMode },
    },
  ];

  // Booking Detail Fields
  const bookingDetailFields = [
    // Account Type
    {
      field_name: "is_organization",
      label: "Account Type",
      type: "select",
      props: { disabled: isRenewMode },
      options_data: [
        addSelectItem({ label: INDIVIDUAL, value: false }),
        addSelectItem({ label: ORGANISATION, value: true }),
      ],
    },

    {
      type: "select",
      field_name: "booking_type",
      required: true,
      props: { disabled: isRenewMode },
      options_data: [
        {
          value: RESERVATION,
          description: "Reserve dedicated parking area",
        },
        { value: STICKER, description: "Buy parking sticker" },
        {
          value: ADVANCE,
          label: "E-coupon",
          description: "Make payment in advance",
        },
      ].map((data) => addSelectItem(data)),
    },

    // City Selection
    {
      field_name: "city",
      label: "City",
      type: "select",
      props: {
        required: true,
        disabled: isRenewMode,
      },
      options_data: filteredCities,
      loading: loadingCities,
      // value: form?.city,
    },

    {
      field_name: "type",
      type: "select",
      label: "Reservation Type",
      props: { required: true },
      options_data: reservationTypesData?.results || reservationTypesData,
      loading: loadingReservationTypes,
      visible: form?.booking_type === RESERVATION,
    },
    {
      field_name: "type",
      type: "select",
      label: "Sticker Type",
      props: { required: true },
      options_data: stickerTypesData?.results || stickerTypesData,
      loading: loadingStickerTypes,
      visible: form?.booking_type === STICKER,
    },

    {
      field_name: "street",
      label: "Street",
      type: "select",
      props: { required: true, disabled: isRenewMode },
      options_data: streetsData,
      loading: loadingStreets,
      visible: booking_type === RESERVATION,
    },

    {
      field_name: "reserved_parkings",
      label: "Park Name",
      type: "select",
      props: { required: booking_type === RESERVATION, disabled: isRenewMode },
      options_data: parkingsUnderStreet?.results?.map((parking) => ({
        ...parking,
        description: `${parking.name} (${parking.capacity - (parking.occupied_slots || 0)}/${parking.capacity} slots available)`,
      })),
      loading: loadingParkingsUnderStreet,
      visible: booking_type === RESERVATION && !!street,
    },

    // Number of Slots field - now an input field
    {
      field_name: "number_of_slots",
      label: "Number of Slots",
      type: "number",
      props: {
        required: true,
        min: 1,
        placeholder: "Enter number of slots",
        disabled: isRenewMode,
      },
      visible: booking_type === RESERVATION,
    },
    {
      field_name: "duration_type",
      label: "Duration Type",
      type: "select",
      props: { required: true },
      options_data: [
        { value: MONTHLY, description: "Months" },
        { value: WEEKLY, description: "Weeks" },
        { value: DAILY, description: "Days" },
      ].map((data) => addSelectItem(data)),
      visible: booking_type !== ADVANCE,
    },

    {
      field_name: "duration",
      label: `Duration (${form.duration_type ? resolveDurationType(form.duration_type) : "Days"})`,
      type: "number",
      props: {
        required: true,
        placeholder:
          booking_type === STICKER || booking_type == RESERVATION
            ? `duration in ${form.duration_type ? resolveDurationType(form.duration_type) : "Days"}`
            : "Parking time in number of days",
      },
      visible: booking_type !== ADVANCE,
    },

    {
      field_name: "start_date",
      label: "Start Date",
      type: "date",
      props: {
        required:
          booking_type === RESERVATION || booking_type === STICKER
            ? true
            : false,
        // disablePast: true,
      },
      visible: booking_type === RESERVATION || booking_type === STICKER,
    },

    {
      field_name: "end_date",
      label: "End Date",
      type: "date",
      props: {
        required: false,
        disablePast: true,
        disabled: true,
        // Remove this line: value: form?.calculatedEndDate,
      },
      visible: booking_type === RESERVATION || booking_type === STICKER, // Make it visible for both types
    },

    // // Backend Number of Slots field - auto-filled and disabled
    // {
    //   field_name: "backend_number_of_slots",
    //   label: "Slots Occupied Due to Car Size",
    //   type: "number",
    //   props: {
    //     disabled: true,
    //     value: backendSlots,
    //     placeholder: "Auto-filled based on car size",
    //   },

    //   visible: booking_type === (RESERVATION && !!car_size),
    // },

    // FIXED: Booking rate field with proper value handling
    {
      field_name: "booking_rate",
      label: "Amount Per Slot Per Day",
      type: "number",
      props: {
        // required: true,
        min: 0,
        step: 0.01,
        placeholder: loadingParkingRateForCarSizeAndParking
          ? "Loading rate..."
          : car_size && reserved_parkings
            ? "Rate will be auto-filled"
            : "Select the parking first",
        disabled: true,
        value: booking_rate || parkingRateAmount || "",
      },
      loading: loadingParkingRateForCarSizeAndParking,
      visible: booking_type === RESERVATION,
    },

    {
      field_name: "number_of_stickers",
      label: "Number of Stickers",
      type: "number",
      value: 1,
      props: {
        // required: true,
        min: 1,
        placeholder: "Enter number of stickers",
        disabled: true,
      },
      visible: booking_type === STICKER,
    },

    {
      field_name: "car_number",
      label: "Car Number Plate",
      type: "text",
      props: {
        required: true,
        placeholder: "Enter car number plate",
        disabled: isRenewMode,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const upperValue = e.target.value.toUpperCase();
          if (updateFormValues) {
            updateFormValues({ car_number: upperValue });
          }
        },
      },
      visible: booking_type === STICKER || booking_type === ADVANCE,
    },
    {
      field_name: "car_type",
      label: "Car Type",
      type: "select",
      props: {
        required: true,
        placeholder: "Enter car type",
        disabled: isRenewMode,
        options:
          carTypes?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],
      },
      visible: booking_type === STICKER,
    },
    // Calculated field for slot amount (Number of Slots × Backend Slots × Booking Rate)
    {
      field_name: "slot_amount",
      label:
        booking_type == STICKER
          ? "Amount Per Sticker"
          : "Slot Amount (Amount Per Slot Per Day × Number of Slots)",
      type: "number",
      props: {
        disabled: true,
        value: slotAmount.toFixed(2),
        showCommas: true,
        placeholder: "Auto-calculated",
      },
      // visible: false,
      visible: booking_type === RESERVATION || booking_type === STICKER,
    },

    // Total Amount Payable field (Slot Amount x Duration)
    {
      field_name: "total_amount_payable",
      label:
        booking_type == STICKER
          ? "Total Amount Payable"
          : "Total Amount Payable",
      type: "number",
      props: {
        disabled: true,
        value: Math.round(totalAmountPayable).toString(),
        showCommas: true,
        placeholder: "Auto-calculated",
      },
      visible:
        booking_type === RESERVATION ||
        (booking_type === STICKER && slotAmount > 0),
    },

    {
      field_name: "amount",
      label: "Amount",
      type: "number",
      props: {
        required: true,
        minValue: form?.booking_type === ADVANCE ? 5000 : undefined,
        step: 0.01,
        maxValue: form?.booking_type === ADVANCE ? 100000 : undefined,
        placeholder: "Enter amount",
        disabled: isRenewMode,
      },
      visible: booking_type === ADVANCE,
    },
  ];

  // Combine all fields
  const fields = [...clientDetailFields, ...bookingDetailFields];

  // Create sections with the field arrays
  const sections = [
    {
      title: "Client Details",
      fields: clientDetailFields,
    },
    {
      title: "Booking Details",
      fields: bookingDetailFields,
    },
  ];

  const redirect = getRegister(URL_BOOKINGS);

  const defaultValues = {
    is_organization: false,
    duration: form?.booking_type !== ADVANCE ? form?.duration : 30,
  };

  return {
    fields,
    sections,
    redirect,
    defaultValues,
    slotAmount,
    totalAmountPayable,
    selectedParking,
    availableSlots,
    totalCapacity,
    occupiedSlots,
    parkingRatesData,
    loadingParkingRateForCarSizeAndParking,
    parkingRatesError,
    parkingRateAmount,
    carTypes,
    loadingCarTypes,
  };
};
